<template>
    <div class="wan-ai-page">
        <!-- Image Generator Section -->
        <section ref="generatorSection" class="generator-section">
            <div class="container">
                <div class="generator-header">
                    <h1 class="generator-title">
                        {{ t('wanAi.generatorTitle') }}
                    </h1>
                    <p class="generator-subtitle">
                        {{ t('wanAi.generatorSubtitle') }}
                    </p>
                </div>

                <ImageGenerator model-name="Wan 2.5" :default-tab="1" @generate="handleImageGenerate"
                    model-key="WAN_2_5" />
            </div>
        </section>

        <!-- Core Features Section -->
        <section class="core-features-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('wanAi.coreFeaturesTitle') }}
                    </h2>
                    <p class="section-subtitle">
                        {{ t('wanAi.coreFeaturesSubtitle') }}
                    </p>
                </div>

                <div class="features-showcase-container">
                    <!-- High-Quality Image Generation Feature -->
                    <div class="feature-showcase">
                        <div class="feature-content">
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('wanAi.precisePromptTitle') }}
                                </h3>
                                <p class="feature-description">
                                    {{ t('wanAi.precisePromptDesc') }}
                                </p>
                            </div>
                            <div class="feature-image">
                                <div class="image-container">
                                    <img src="https://static.weryai.com/growth/image/32918399cf9a77da5f1b4e324dfeab1b.png"
                                        :alt="t('wanAi.precisePromptAlt')" loading="lazy"
                                        class="feature-image-preview" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Creative Expression Feature (reverse layout) -->
                    <div class="feature-showcase reverse">
                        <div class="feature-content">
                            <div class="feature-image">
                                <div class="image-container">
                                    <img src="https://static.weryai.com/growth/image/a9632f5ea8e1f550dfe17914dda21bb0.png"
                                        :alt="t('wanAi.photoRealisticAlt')" loading="lazy"
                                        class="feature-image-preview" />
                                </div>
                            </div>
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('wanAi.photoRealisticTitle') }}
                                </h3>
                                <p class="feature-description">
                                    {{ t('wanAi.photoRealisticDesc') }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Advanced Style Control Feature -->
                    <div class="feature-showcase">
                        <div class="feature-content">
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('wanAi.diverseStylesTitle') }}
                                </h3>
                                <p class="feature-description">
                                    {{ t('wanAi.diverseStylesDesc') }}
                                </p>
                            </div>
                            <div class="feature-image">
                                <div class="image-container">
                                    <img src="https://static.weryai.com/growth/image/2b9ee27db68fb9594cc4cba3618203ac.png"
                                        :alt="t('wanAi.diverseStylesAlt')" loading="lazy"
                                        class="feature-image-preview" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Multi-Modal Fusion Feature (reverse layout) -->
                    <div class="feature-showcase reverse">
                        <div class="feature-content">
                            <div class="feature-image">
                                <div class="image-container">
                                    <img src="https://static.weryai.com/growth/image/ca2cacf1166368db3170e70949eb8536.png"
                                        :alt="t('wanAi.creativeTextAlt')" loading="lazy"
                                        class="feature-image-preview" />
                                </div>
                            </div>
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('wanAi.creativeTextTitle') }}
                                </h3>
                                <p class="feature-description">
                                    {{ t('wanAi.creativeTextDesc') }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Consistent Elements Feature -->
                    <div class="feature-showcase">
                        <div class="feature-content">
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('wanAi.consistentElementsTitle') }}
                                </h3>
                                <p class="feature-description">
                                    {{ t('wanAi.consistentElementsDesc') }}
                                </p>
                            </div>
                            <div class="feature-image">
                                <div class="image-container">
                                    <img src="https://static.weryai.com/growth/image/526741dadd337ae8757c194d17bc9b4e.png"
                                        :alt="t('wanAi.consistentElementsAlt')" loading="lazy"
                                        class="feature-image-preview" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Precise Image Editing Feature (reverse layout) -->
                    <div class="feature-showcase reverse">
                        <div class="feature-content">
                            <div class="feature-image">
                                <div class="image-container">
                                    <img src="https://static.weryai.com/growth/image/c59f80b0addc327dd349b98e0acf7e69.png"
                                        :alt="t('wanAi.preciseEditingAlt')" loading="lazy"
                                        class="feature-image-preview" />
                                </div>
                            </div>
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('wanAi.preciseEditingTitle') }}
                                </h3>
                                <p class="feature-description">
                                    {{ t('wanAi.preciseEditingDesc') }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- How to Use Section -->
        <section class="how-to-use-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('wanAi.howToUseTitle') }}
                    </h2>
                    <p class="section-subtitle">
                        {{ t('wanAi.howToUseSubtitle') }}
                    </p>
                </div>

                <ol class="steps-container">
                    <li class="step-item">
                        <div class="step-number">1</div>
                        <h3 class="step-title">{{ t('wanAi.step1Title') }}</h3>
                        <p class="step-description">
                            {{ t('wanAi.step1Description') }}
                        </p>
                    </li>

                    <li class="step-item">
                        <div class="step-number">2</div>
                        <h3 class="step-title">{{ t('wanAi.step2Title') }}</h3>
                        <p class="step-description">
                            {{ t('wanAi.step2Description') }}
                        </p>
                    </li>

                    <li class="step-item">
                        <div class="step-number">3</div>
                        <h3 class="step-title">{{ t('wanAi.step3Title') }}</h3>
                        <p class="step-description">
                            {{ t('wanAi.step3Description') }}
                        </p>
                    </li>
                </ol>
            </div>
        </section>

        <!-- YouTube Videos Section -->
        <YouTubeCarouselSection :title="t('wanAi.youtubeTitle')" :videos="youtubeVideos" />

        <!-- X Posts Section -->
        <TwitterPosts :title="t('wanAi.xPostsTitle')" :tweet-urls="twitterTweetUrls" />

        <!-- Explore Other Models Section -->
        <section class="explore-models-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('wanAi.exploreModelsTitle') }}
                    </h2>
                </div>

                <div class="models-grid">
                    <NuxtLink to="/image/nano-banana" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/cms/3ed8c9e1a1ec3b37133c608c292821cb.webp"
                                :alt="t('wanAi.model1Title')" width="40" height="40" loading="lazy" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('wanAi.model1Title') }}
                        </h3>
                    </NuxtLink>

                    <NuxtLink to="/image/flux" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/cms/a4d5bf7d150f6738f42d3d628ac39869.webp"
                                :alt="t('wanAi.model2Title')" width="40" height="40" loading="lazy" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('wanAi.model2Title') }}
                        </h3>
                    </NuxtLink>

                    <NuxtLink to="/image/imagen" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/cms/8ec333d0fa59f3ebf4a2373592ae76a6.webp"
                                :alt="t('wanAi.model3Title')" width="40" height="40" loading="lazy" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('wanAi.model3Title') }}
                        </h3>
                    </NuxtLink>
                </div>
            </div>
        </section>

        <!-- FAQ Section -->
        <section class="faq-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">{{ t('wanAi.faqTitle') }}</h2>
                </div>

                <div class="faq-list">
                    <div v-for="(faq, index) in faqData" :key="index" class="faq-item"
                        :class="{ active: activeFaqIndex === index }">
                        <div class="faq-question" @click="toggleFaq(index)">
                            <!-- <span class="faq-icon">></span> -->
                            <span class="faq-text">{{ t(faq.question) }}</span>
                        </div>
                        <div class="faq-answer">
                            <p>{{ t(faq.answer) }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- CTA Section -->
        <section class="cta-section">
            <div class="container">
                <div class="cta-content">
                    <!-- 微鲤AI Logo -->
                    <div class="cta-logo">
                        <div class="logo-icon logo-image">
                            <img src="https://static.weryai.com/growth/image/4608e0a35205ff929dba2ea3c51f6e62.webp"
                                alt="微鲤AI" class="logo-image" loading="lazy" />
                        </div>
                    </div>

                    <!-- Main Content -->
                    <div class="cta-header">
                        <h2 class="cta-title">{{ t('wanAi.ctaTitle') }}</h2>
                        <p class="cta-description">
                            {{ t('wanAi.ctaDescription') }}
                        </p>
                    </div>

                    <!-- CTA Button -->
                    <div class="cta-actions">
                        <button @click="scrollToGenerator" class="cta-primary-btn">
                            {{ t('wanAi.ctaButton') }}
                        </button>

                        <p class="cta-disclaimer">
                            {{ t('wanAi.ctaDisclaimer') }}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useHead } from '#imports'
import ImageGenerator from '~/components/ImageGenerator.vue'
import TwitterPosts from '~/components/TwitterPosts.vue'
import YouTubeCarouselSection from '~/components/model/video/YouTubeCarouselSection.vue'
import { useI18n } from '~/composables/useI18n'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import { useHowToSeo } from '~/composables/useHowToSeo'
import { analyticsEvent } from '~/utils/log-event-func'

definePageMeta({
    layout: 'website',
})

// 多语言支持
const { t, locale } = useI18n()

// 使用多语言SEO系统
useMultilingualSeo({
    pageType: 'image_wan_ai',
})

// Ref for generator section
const generatorSection = ref<HTMLElement | null>(null)

// Scroll to generator function
const scrollToGenerator = () => {
    if (generatorSection.value) {
        generatorSection.value.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        })
    }
}

// YouTube Videos Data
const youtubeVideos = ref([
    {
        title: 'Wan AI Video 1',
        embedUrl: 'https://www.youtube.com/embed/2YEvXE4t8FM',
    },
    {
        title: 'Wan AI Video 2',
        embedUrl: 'https://www.youtube.com/embed/E7eWvr-RvwM',
    },
    {
        title: 'Wan AI Video 3',
        embedUrl: 'https://www.youtube.com/embed/m7cdSfIiALE',
    },
    {
        title: 'Wan AI Video 4',
        embedUrl: 'https://www.youtube.com/embed/oSXeTyHIN2E',
    },
])

// Twitter Tweet URLs Data
const twitterTweetUrls = [
    'https://twitter.com/AlibabaGroup/status/1980502557937274964',
    'https://twitter.com/awanchuang/status/1983062064693461200',
    'https://twitter.com/alibaba_cloud/status/1982604186106605579',
    'https://twitter.com/KanaWorks_AI/status/1977594008055795757',
    'https://twitter.com/Alibaba_Wan/status/1972107988992512083',
    'https://twitter.com/so_ainsight/status/1973188301202006514',
    'https://twitter.com/D_studioproject/status/1972224333742588193',
    'https://twitter.com/Alibaba_Wan/status/1972532092228809151',
    'https://twitter.com/carlosbrlhnt/status/1983210506354586108',
    'https://twitter.com/sebatheepan/status/1975359673349059019',
]

// HowTo SEO - 为"如何使用"步骤添加结构化数据
const howToUseSteps = computed(() => [
    {
        title: t('wanAi.step1Title'),
        description: t('wanAi.step1Description'),
    },
    {
        title: t('wanAi.step2Title'),
        description: t('wanAi.step2Description'),
    },
    {
        title: t('wanAi.step3Title'),
        description: t('wanAi.step3Description'),
    },
])

// FAQ Data
const faqData = ref([
    {
        question: 'wanAi.faq1Question',
        answer: 'wanAi.faq1Answer',
    },
    {
        question: 'wanAi.faq2Question',
        answer: 'wanAi.faq2Answer',
    },
    {
        question: 'wanAi.faq3Question',
        answer: 'wanAi.faq3Answer',
    },
    {
        question: 'wanAi.faq4Question',
        answer: 'wanAi.faq4Answer',
    },
    {
        question: 'wanAi.faq5Question',
        answer: 'wanAi.faq5Answer',
    },
])

// FAQ SEO - 注入 FAQPage 结构化数据
const faqDataForSeo = computed(() =>
    faqData.value.map(item => ({
        question: t(item.question),
        answer: t(item.answer),
    }))
)

// FAQ Interactive Functions
const activeFaqIndex = ref<number | null>(null)

const toggleFaq = (index: number) => {
    if (activeFaqIndex.value === index) {
        activeFaqIndex.value = null
    } else {
        activeFaqIndex.value = index
    }
}

// Image Generation Handler
const handleImageGenerate = (data: {
    prompt: string
    image?: File
    tabType: 'text-to-image' | 'image-to-image'
}) => {
    // Add actual image generation logic here
    // For example, call API, show loading state, etc
}

onMounted(() => {
    analyticsEvent('view_model_page_common', {
        model_name: 'wan_2_5',
        type: 'image',
    })
})

// --- SEO 结构化数据注入 (确保在变量定义之后) ---

const requestUrlHref = useRequestURL().href

// HowTo SEO
useHowToSeo(
    computed(() => t('wanAi.howToUseTitle')),
    howToUseSteps
)

// 注意：FAQPage 已由 useMultilingualSeo 注入，无需重复调用 useFaqSeo

// BreadcrumbList SEO
useHead({
    script: [
        {
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        {
                            '@type': 'ListItem',
                            position: 1,
                            name: 'Home',
                            item: 'https://www.weryai.com.cn/',
                        },
                        {
                            '@type': 'ListItem',
                            position: 2,
                            name: t('wanAi.generatorTitle'),
                            item: requestUrlHref,
                        },
                    ],
                })
            ),
        },
    ],
})
</script>

<style lang="less" scoped>
@import '~/assets/css/howToUse.css';
</style>
